import React, { useEffect, useMemo, useRef } from "react";
import { Outlet } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiLock, FiSave } from "react-icons/fi";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useOnboardingFlow } from "./OnboardingFlowContext";
import { getOnboardingMessages } from "./onboardingI18n";
import "./OnboardingApp.css";

function hexToRgba(hex, alpha) {
  const normalized = String(hex || "").replace("#", "").trim();

  if (![3, 6].includes(normalized.length)) {
    return hex;
  }

  const fullHex =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;

  const intValue = Number.parseInt(fullHex, 16);
  const r = (intValue >> 16) & 255;
  const g = (intValue >> 8) & 255;
  const b = intValue & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildOnboardingThemeVars(theme, themeId) {
  const preview = theme?.preview || {};
  const isLight = themeId === "light";

  return {
    "--onb-shell-background": `
      radial-gradient(900px 420px at 0% 0%, ${hexToRgba(preview.sidebarAccent, isLight ? 0.16 : 0.18)}, transparent 62%),
      radial-gradient(760px 420px at 100% 0%, ${hexToRgba(preview.highlight, isLight ? 0.14 : 0.14)}, transparent 58%),
      linear-gradient(180deg, ${preview.shell || "#080b16"} 0%, ${preview.surfaceAlt || "#050910"} 100%)
    `,
    "--onb-shell-text": preview.text || "#ecf0f7",
    "--onb-card-border": hexToRgba(preview.border || "#384260", isLight ? 0.72 : 0.35),
    "--onb-card-background": isLight
      ? `linear-gradient(180deg, ${hexToRgba(preview.surface || "#fcf9f3", 0.96)}, ${hexToRgba(
          preview.surfaceAlt || "#f0eade",
          0.94
        )})`
      : hexToRgba(preview.surface || "#0f1422", 0.88),
    "--onb-card-shadow": isLight
      ? "0 26px 70px rgba(86, 65, 39, 0.16)"
      : "0 26px 70px rgba(0, 0, 0, 0.25)",
    "--onb-backdrop": isLight ? "blur(16px)" : "blur(12px)",
    "--onb-brand-badge-background": `linear-gradient(135deg, ${hexToRgba(
      preview.sidebarAccent || "#30d8ff",
      isLight ? 0.18 : 0.2
    )}, ${hexToRgba(preview.highlight || "#ffc857", isLight ? 0.28 : 0.28)})`,
    "--onb-brand-badge-text": isLight ? preview.text || "#183645" : "#ffffff",
    "--onb-heading-color": isLight ? "#163746" : preview.highlight || "#ffc857",
    "--onb-eyebrow-color": hexToRgba(preview.text || "#ecf0f7", isLight ? 0.54 : 0.58),
    "--onb-meta-label-color": hexToRgba(preview.text || "#ecf0f7", isLight ? 0.58 : 0.55),
    "--onb-body-text": hexToRgba(preview.text || "#ecf0f7", isLight ? 0.76 : 0.72),
    "--onb-soft-background": isLight ? "rgba(255, 255, 255, 0.62)" : "rgba(255, 255, 255, 0.06)",
    "--onb-progress-track": isLight ? "rgba(24, 54, 69, 0.12)" : "rgba(255, 255, 255, 0.1)",
    "--onb-progress-fill": preview.sidebarAccent || "#30d8ff",
    "--onb-progress-fill-gradient": `linear-gradient(135deg, ${preview.sidebarAccent || "#30d8ff"} 0%, ${
      preview.highlight || "#ffc857"
    } 100%)`,
    "--onb-progress-inner": isLight
      ? hexToRgba(preview.surface || "#fcf9f3", 0.94)
      : hexToRgba(preview.surfaceAlt || "#171d31", 0.94),
    "--onb-progress-glow": hexToRgba(preview.sidebarAccent || "#30d8ff", isLight ? 0.2 : 0.24),
    "--onb-secondary-background": isLight ? "rgba(255, 255, 255, 0.7)" : "rgba(255, 255, 255, 0.06)",
    "--onb-secondary-text": preview.text || "#ecf0f7",
    "--onb-primary-background": `linear-gradient(135deg, ${preview.sidebarAccent || "#30d8ff"} 0%, ${
      preview.highlight || "#ffc857"
    } 100%)`,
    "--onb-primary-text": isLight ? "#faf6ee" : "#07111b",
    "--onb-ghost-background": hexToRgba(preview.sidebarAccent || "#30d8ff", isLight ? 0.14 : 0.1),
    "--onb-ghost-text": preview.text || "#d9ecfb",
    "--onb-danger-background": isLight ? "rgba(176, 78, 57, 0.12)" : "rgba(255, 107, 107, 0.16)",
    "--onb-danger-text": isLight ? "#9e452f" : "#ffd3d3",
    "--onb-step-background": isLight ? "rgba(255, 255, 255, 0.62)" : "rgba(255, 255, 255, 0.03)",
    "--onb-step-hover-border": hexToRgba(preview.sidebarAccent || "#30d8ff", isLight ? 0.32 : 0.24),
    "--onb-step-hover-background": hexToRgba(preview.sidebarAccent || "#30d8ff", isLight ? 0.1 : 0.08),
    "--onb-step-active-border": hexToRgba(preview.sidebarAccent || "#30d8ff", isLight ? 0.52 : 0.44),
    "--onb-step-active-background": hexToRgba(preview.sidebarAccent || "#30d8ff", isLight ? 0.2 : 0.17),
    "--onb-step-complete-surface": hexToRgba(preview.success || "#2bdc88", isLight ? 0.12 : 0.08),
    "--onb-step-complete-background": hexToRgba(preview.success || "#2bdc88", isLight ? 0.24 : 0.16),
    "--onb-step-complete-text": isLight ? "#2e6247" : "#d9ffd2",
    "--onb-step-complete-border": hexToRgba(preview.success || "#2bdc88", isLight ? 0.54 : 0.32),
    "--onb-badge-text": hexToRgba(preview.text || "#ecf0f7", isLight ? 0.86 : 0.82),
    "--onb-muted-text": hexToRgba(preview.text || "#ecf0f7", isLight ? 0.66 : 0.62),
    "--onb-input-border": hexToRgba(preview.border || "#384260", isLight ? 0.78 : 0.35),
    "--onb-input-background": isLight ? "rgba(255, 255, 255, 0.78)" : "rgba(255, 255, 255, 0.04)",
    "--onb-input-text": preview.text || "#ecf0f7",
    "--onb-select-surface": isLight ? "#ffffff" : "#05070a",
    "--onb-select-text": isLight ? "#101418" : "#ffffff",
    "--onb-select-color-scheme": isLight ? "light" : "dark",
    "--onb-input-focus-border": hexToRgba(preview.sidebarAccent || "#30d8ff", isLight ? 0.5 : 0.42),
    "--onb-input-focus-shadow": `0 0 0 3px ${hexToRgba(
      preview.sidebarAccent || "#30d8ff",
      isLight ? 0.16 : 0.12
    )}`,
    "--onb-success-background": hexToRgba(preview.success || "#2bdc88", isLight ? 0.18 : 0.16),
    "--onb-success-text": isLight ? "#426a55" : "#d9ffd2",
    "--onb-error-background": isLight ? "rgba(176, 78, 57, 0.12)" : "rgba(255, 107, 107, 0.16)",
    "--onb-error-text": isLight ? "#9e452f" : "#ffd8d8",
    "--onb-highlight-tag-background": hexToRgba(preview.highlight || "#ffc857", isLight ? 0.22 : 0.18),
    "--onb-highlight-tag-text": isLight ? "#7b5a1b" : "#ffe0d0",
    "--onb-point-gradient": `linear-gradient(135deg, ${preview.sidebarAccent || "#30d8ff"} 0%, ${
      preview.highlight || "#ffc857"
    } 100%)`,
    "--onb-table-head-bg": isLight
      ? hexToRgba(preview.surfaceAlt || "#f0eade", 0.97)
      : hexToRgba(preview.surface || "#0f1422", 0.97),
  };
}

export default function OnboardingPanel() {
  const stepListRef = useRef(null);
  const stepItemRefs = useRef({});
  const { languageId, themeId, theme } = usePmsTheme();
  const {
    steps,
    completedSteps,
    currentStepId,
    currentStepCommandState,
    isStepAccessible,
    isStepComplete,
    goToStep,
  } = useOnboardingFlow();
  const copy = getOnboardingMessages(languageId);
  const currentStepIndex = Math.max(
    steps.findIndex((step) => step.id === currentStepId),
    0
  );
  const previousStep = currentStepIndex > 0 ? steps[currentStepIndex - 1] : null;
  const nextStep = currentStepIndex < steps.length - 1 ? steps[currentStepIndex + 1] : null;
  const totalSteps = Math.max(steps.length, 1);
  const completedCount = Math.min(completedSteps.length, totalSteps);
  const progressPercent = Math.round((completedCount / totalSteps) * 100);
  const hasCommandState = Boolean(currentStepCommandState);
  const isStepBusy = Boolean(currentStepCommandState?.isSaving);
  const canGoPrevious = Boolean(previousStep) && !isStepBusy;
  const canGoNext =
    Boolean(nextStep) &&
    hasCommandState &&
    !isStepBusy &&
    isStepAccessible(nextStep.id) &&
    currentStepCommandState?.canProceed !== false;
  const canSave =
    hasCommandState &&
    typeof currentStepCommandState?.onSave === "function" &&
    currentStepCommandState?.canSave !== false &&
    !isStepBusy;
  const onboardingThemeVars = useMemo(
    () => ({
      ...buildOnboardingThemeVars(theme, themeId),
      "--onb-progress-value": `${progressPercent}%`,
    }),
    [progressPercent, theme, themeId]
  );

  useEffect(() => {
    const listElement = stepListRef.current;
    const activeItemElement = stepItemRefs.current[currentStepId];

    if (!listElement || !activeItemElement) {
      return;
    }

    const computedStyle = window.getComputedStyle(listElement);
    const canScroll = ["auto", "scroll", "overlay"].includes(computedStyle.overflowY);

    if (!canScroll || listElement.scrollHeight <= listElement.clientHeight) {
      return;
    }

    const listRect = listElement.getBoundingClientRect();
    const itemRect = activeItemElement.getBoundingClientRect();
    const topOffset = 6;
    const targetScrollTop =
      listElement.scrollTop + (itemRect.top - listRect.top) - topOffset;
    const boundedScrollTop = Math.min(
      Math.max(0, targetScrollTop),
      Math.max(0, listElement.scrollHeight - listElement.clientHeight)
    );

    listElement.scrollTo({
      top: boundedScrollTop,
      behavior: "smooth",
    });
  }, [currentStepId]);

  return (
    <div className="onboarding-shell" data-theme={themeId} style={onboardingThemeVars}>
      <aside className="onboarding-rail">
        <div className="onboarding-brand">
          <div className="onboarding-brandBadge">OB</div>
          <div>
            <div className="onboarding-eyebrow">{copy.panel.eyebrow}</div>
            <h1 className="onboarding-brandTitle">Onboarding</h1>
          </div>
        </div>

        <div className="onboarding-stepSection">
          <div className="onboarding-metaLabel">{copy.panel.stepsTitle}</div>
          <div ref={stepListRef} className="onboarding-stepList">
            {steps.map((step, index) => {
              const stepCopy = copy.steps[step.id];
              const isActive = currentStepId === step.id;
              const isComplete = isStepComplete(step.id);
              const isAccessible = isStepAccessible(step.id);

              return (
                <button
                  key={step.id}
                  ref={(element) => {
                    if (element) {
                      stepItemRefs.current[step.id] = element;
                    } else {
                      delete stepItemRefs.current[step.id];
                    }
                  }}
                  type="button"
                  className={`onboarding-stepItem ${
                    isActive ? "onboarding-stepItem--active" : ""
                  } ${isComplete ? "onboarding-stepItem--complete" : ""} ${
                    !isAccessible ? "onboarding-stepItem--locked" : ""
                  }`}
                  disabled={!isAccessible}
                  onClick={() => goToStep(step.id)}
                >
                  <span className="onboarding-stepIndex">
                    {isComplete ? <FiCheckCircle size={16} /> : index + 1}
                  </span>
                  <span className="onboarding-stepCopy">
                    <strong>{stepCopy.title}</strong>
                    <small>{stepCopy.description}</small>
                  </span>
                  <span className="onboarding-stepState">
                    {isActive
                      ? copy.panel.currentBadge
                      : isComplete
                        ? copy.panel.doneBadge
                        : isAccessible
                          ? copy.panel.readyBadge
                          : copy.panel.lockedBadge}
                    {!isAccessible ? <FiLock size={12} /> : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      <main className="onboarding-main">
        <header className="onboarding-headerCard">
          <div className="onboarding-headerLayout">
            <div>
              <div className="onboarding-eyebrow">
                {copy.progress.stepCounter(currentStepIndex + 1, steps.length)}
              </div>
              <h2 className="onboarding-pageTitle">{copy.panel.title}</h2>
              <p className="onboarding-pageText">{copy.panel.copy}</p>
            </div>

            <div className="onboarding-headerControls">
              <section className="onboarding-commandCenter" aria-label={copy.panel.stepsTitle}>
                <button
                  type="button"
                  className="onboarding-secondaryBtn onboarding-commandBtn"
                  disabled={!canGoPrevious}
                  onClick={() => previousStep && goToStep(previousStep.id)}
                >
                  <FiArrowLeft size={15} />
                  {copy.panel.previousAction}
                </button>
                <button
                  type="button"
                  className="onboarding-primaryBtn onboarding-commandBtn"
                  disabled={!canGoNext}
                  onClick={() => nextStep && goToStep(nextStep.id)}
                >
                  {copy.panel.nextAction}
                  <FiArrowRight size={15} />
                </button>
                <button
                  type="button"
                  className="onboarding-secondaryBtn onboarding-commandBtn"
                  disabled={!canSave}
                  onClick={() => currentStepCommandState?.onSave?.()}
                >
                  <FiSave size={15} />
                  {copy.panel.saveAction}
                </button>
              </section>

              <section
                className="onboarding-progressCard onboarding-progressCard--header"
                aria-label={`${copy.panel.progressTitle}: ${copy.panel.progressCompleted(
                  completedCount,
                  totalSteps
                )}`}
                title={copy.panel.progressCompleted(completedCount, totalSteps)}
              >
                <div className="onboarding-progressRing" aria-hidden="true">
                  <div className="onboarding-progressRingInner">
                    <strong>{progressPercent}%</strong>
                    <span>{copy.panel.progressDoneLabel}</span>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </header>

        <section className="onboarding-content">
          <Outlet />
        </section>
      </main>
    </div>
  );
}
